import classNames from "./ConfigureTab.module.css";
import { app, pages } from "@microsoft/teams-js";
import { Image } from "@fluentui/react-components";
import useUser, { useTeamsLogin } from "../../hooks/user";
import { useEffect, useMemo, useRef, useState } from "react";
import Loader from "../common/Loader";
import { LocalStorageKeys } from "../../types";
import { useTeamsContext } from "../../hooks/teams";
import CreateOrEditRoom from "../routes/room/CreateOrEditRoom";
import Button from "../common/Button";
import SupportDialog from "../common/dialogs/SupportDialog";
import * as Sentry from "@sentry/react";

const generateDeepLink = (params: {
  appId: string;
  tabId: string;
  context: { chatId: string; contextType: "chat" };
}) => {
  const baseUrl = "https://teams.microsoft.com/l/entity/";

  // Create a URL object
  const url = new URL(baseUrl + params.appId + "/" + params.tabId);

  // Set query parameters
  url.searchParams.set("webUrl", "https://teams.byrdhouseapp.com/#/tab");
  url.searchParams.set("label", "Langfinity AI Translation");
  url.searchParams.set("context", JSON.stringify(params.context));
  url.searchParams.set("openInMeeting", "true");

  return url.toString();
};

export default function ConfigureTab() {
  const [error, setError] = useState<string>();

  // Set the save handler
  const teamsContext = useTeamsContext();

  const tabId = "byrdhouse-tab-id";

  const deepLink = useMemo(() => {
    if (!teamsContext?.chat?.id) {
      return;
    }
    return generateDeepLink({
      // Hardcoded app id TODO: Way to get this programmatically?
      appId: "69cb19e0-cc93-4557-974b-6f48d7803655",
      tabId: tabId,
      context: {
        chatId: teamsContext.chat.id,
        contextType: "chat",
      },
    });
  }, [teamsContext?.chat?.id]);

  const [triggerSubmit, setTriggerSubmit] = useState(0);

  const saveEventRef = useRef<pages.config.SaveEvent>();

  useEffect(() => {
    const onSaveHandler = async (saveEvent: pages.config.SaveEvent) => {
      try {
        setError(undefined);
        saveEventRef.current = saveEvent;
        setTriggerSubmit((prevState) => prevState + 1);
      } catch (error) {
        console.error(error);
        setError(
          "Something went wrong when adding Langfinity to this meeting, please try again."
        );
      }
    };

    pages.config.registerOnSaveHandler(onSaveHandler);
    app.notifySuccess();
  }, [teamsContext?.chat?.id]);

  const [user] = useUser();
  const { login, loading } = useTeamsLogin();

  useEffect(() => {
    if (user.auth) {
      pages.config.setValidityState(true);
    }
  }, [user.auth]);

  const [showSupportDialog, setShowSupportDialog] = useState(false);

  if (
    (localStorage.getItem(LocalStorageKeys.EXPECT_LOGIN) && !user.auth) ||
    loading
  ) {
    return <Loader />;
  }

  if (user.auth) {
    return (
      <div className={`${classNames.ConfigureTab} ${classNames.CreateOrEdit}`}>
        <CreateOrEditRoom
          roomType="teams"
          teamsParams={{
            deep_link: deepLink,
            teams_chat_id: teamsContext?.chat?.id,
          }}
          hideSubmit={true}
          triggerSubmit={triggerSubmit}
          onCreatedOrUpdated={async (response) => {
            await pages.config.setConfig({
              contentUrl: `${window.location.origin}/#/teams/room/${response.data.room.id}`,
              websiteUrl: `${window.location.origin}/#/teams/room/${response.data.room.id}`,
              removeUrl: `${window.location.origin}/#/teams/room/${response.data.room.id}/remove`,
              suggestedDisplayName: "Langfinity AI Translation",
              entityId: tabId,
            });

            saveEventRef.current?.notifySuccess();
          }}
          onInvalid={() => {
            saveEventRef.current?.notifyFailure();
            saveEventRef.current = undefined;
          }}
          onError={(error) => {
            saveEventRef.current?.notifyFailure();
            saveEventRef.current = undefined;
            console.error(error);
            Sentry.captureException(error);
            setError(
              "Something went wrong when adding Langfinity to this meeting, please try again."
            );
          }}
        />
        {error && <div style={{ color: "red" }}>{error}</div>}
      </div>
    );
  }

  return (
    <>
      <SupportDialog
        open={showSupportDialog}
        onOpenChange={setShowSupportDialog}
      />
      <div className={classNames.ConfigureTab}>
        <div className={classNames.TabConfigHeader}>
          <Image src={"logo.svg"} />
          <h1>Welcome to Langfinity</h1>
          <h2>
            AI-powered, real-time translation in voice and captions for 50+
            languages for your Teams meetings, webinars, and town halls
          </h2>
        </div>

        <Button onClick={() => login()}>Continue with Microsoft</Button>
        <div className={classNames.CreateAccount}>
          <p>
            Don't have an account?{" "}
            <span
              onClick={() => setShowSupportDialog(true)}
              className={classNames.Support}
            >
              Contact Support
            </span>
          </p>
          <Button
            color="gray"
            backgroundColor="black"
            borderColor="dark-gray"
            onClick={() => login()}
          >
            Create an account
          </Button>
        </div>

        <div className={classNames.TabConfigFooter}>
          <p>
            <a
              href="https://byrdhouse.notion.site/byrdhouse/Privacy-Policy-c71b7c579187429da97f8aec36b1585e"
              target="_blank"
              rel="noreferrer"
            >
              Privacy Policy
            </a>{" "}
            <a
              href="https://byrdhouse.notion.site/Terms-of-Service-7d7aa2e356ef4f22bec82a8d885fdc4d"
              target="_blank"
              rel="noreferrer"
            >
              Terms of Service
            </a>
          </p>
          <p>© 2025 BYRDHOUSE INC</p>
        </div>
      </div>
    </>
  );
}
